import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  ArrowRight,
  CheckCircle,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const IntegrationsPage = () => {
  const integrations = [
    {
      category: 'Social Media',
      items: [
        { name: 'Facebook', description: 'Pages, Groups, and personal profiles', logo: 'https://cdn.simpleicons.org/facebook/1877F2', status: 'live' },
        { name: 'Instagram', description: 'Feed posts, Stories, and Reels', logo: 'https://cdn.simpleicons.org/instagram/E4405F', status: 'live' },
        { name: 'Twitter/X', description: 'Tweets, threads, and analytics', logo: 'https://cdn.simpleicons.org/x/000000', status: 'live' },
        { name: 'LinkedIn', description: 'Company pages and personal profiles', logo: 'https://cdn.simpleicons.org/linkedin/0A66C2', status: 'live' },
        { name: 'TikTok', description: 'Video posts and analytics', logo: 'https://cdn.simpleicons.org/tiktok/000000', status: 'live' },
        { name: 'YouTube', description: 'Videos, Shorts, and community posts', logo: 'https://cdn.simpleicons.org/youtube/FF0000', status: 'live' },
        { name: 'Pinterest', description: 'Pins and boards', logo: 'https://cdn.simpleicons.org/pinterest/BD081C', status: 'live' },
        { name: 'Threads', description: 'Text posts and replies', logo: 'https://cdn.simpleicons.org/threads/000000', status: 'live' },
        { name: 'Bluesky', description: 'Posts and feeds', logo: 'https://cdn.simpleicons.org/bluesky/0085FF', status: 'live' },
        { name: 'Snapchat', description: 'Stories and Spotlight', logo: 'https://cdn.simpleicons.org/snapchat/FFFC00', status: 'live' },
        { name: 'Google Business', description: 'Posts and updates', logo: 'https://cdn.simpleicons.org/google/4285F4', status: 'live' }
      ]
    },
    {
      category: 'AI & Content',
      items: [
        { name: 'OpenAI GPT-5', description: 'AI caption and content generation', logo: 'https://cdn.simpleicons.org/openai/412991', status: 'live' },
        { name: 'Sora 2', description: 'AI video generation', logo: 'https://cdn.simpleicons.org/openai/412991', status: 'live' },
        { name: 'DALL-E', description: 'AI image generation', logo: 'https://cdn.simpleicons.org/openai/412991', status: 'coming' }
      ]
    },
    {
      category: 'Payments & Business',
      items: [
        { name: 'Stripe', description: 'Subscription billing and payments', logo: 'https://cdn.simpleicons.org/stripe/635BFF', status: 'live' }
      ]
    },
    {
      category: 'Communication',
      items: [
        { name: 'Resend', description: 'Email notifications', logo: 'https://cdn.simpleicons.org/mail/000000', status: 'live' },
        { name: 'Web Push', description: 'Browser push notifications', logo: 'https://cdn.simpleicons.org/googlechrome/4285F4', status: 'live' },
        { name: 'Slack', description: 'Team notifications', logo: 'https://cdn.simpleicons.org/slack/4A154B', status: 'coming' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/stellisocial-icon.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-xl text-slate-900" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/integrations" className="text-emerald-600 font-medium">Integrations</Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900">Blog</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-emerald-100 text-emerald-700 mb-4">Integrations</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Connect Your
            <span className="text-emerald-500"> Favorite Tools</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            StelliSocial integrates with 15+ platforms to streamline your social media workflow.
          </p>
        </div>
      </section>

      {/* Integrations Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {integrations.map((category, i) => (
            <div key={category.category} className="mb-16">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">{category.category}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {category.items.map((item, j) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: j * 0.05 }}
                  >
                    <Card className="border-slate-100 hover:border-emerald-200 hover:shadow-md transition-all">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden">
                            <img 
                              src={item.logo} 
                              alt={item.name}
                              className="w-6 h-6"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-900">{item.name}</h3>
                              <Badge 
                                variant="secondary" 
                                className={`text-xs ${item.status === 'live' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                              >
                                {item.status === 'live' ? 'Live' : 'Coming Soon'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500 mt-1">{item.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Need a Custom Integration?</h2>
          <p className="text-emerald-100 mb-8">Our API allows you to build custom integrations for your workflow.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/developers">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50">
                View API Docs <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/register">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-600">
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
                <span className="font-bold  text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm">The complete social media management platform for teams.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} StelliSocial. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default IntegrationsPage;
